import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import useWebSocket from '../hooks/useWebSocket';
import { ArrowLeft, Users, FileText, HelpCircle } from 'lucide-react';
import { BulkFamilyUpload } from './admin/BulkFamilyUpload';
import { BulkPricelistUpload } from './admin/BulkPricelistUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [hospitals, setHospitals] = useState([]);
  const [currencies, setCurrencies] = useState({});

  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const username = localStorage.getItem('username');
  const hospitalName = localStorage.getItem('hospital_name');

  const isSuperAdmin = username === 'superadmin' || (role === 'Admin' && hospitalName === 'System Administration');
  const isDataEntry = role === 'DataEntry';
  const isViewOnly = role === 'ViewOnly';

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  const loadData = async () => {
    try {
      const [hospitalsRes, currenciesRes] = await Promise.all([
        axios.get(`${API}/admin/hospitals`, axiosConfig),
        axios.get(`${API}/currencies`, axiosConfig),
      ]);
      setHospitals(hospitalsRes.data);
      const currencyMap = {};
      currenciesRes.data.forEach((c) => { currencyMap[c.code] = c; });
      setCurrencies(currencyMap);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  useWebSocket((message) => {
    if (['deposit_added', 'claim_submitted', 'family_balance_updated'].includes(message.type)) {
      loadData();
    }
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (isViewOnly) {
      toast.error('Access denied. View-only users cannot access Admin Panel.');
      navigate('/dashboard');
      return;
    }
    // Allow only superadmin and DataEntry roles - regular Admin users do NOT have access
    if (!isSuperAdmin && !isDataEntry) {
      toast.error('Access denied. Superadmin or DataEntry role required.');
      navigate('/dashboard');
      return;
    }
    loadData();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f4f8 100%)' }}>
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Admin Panel</h1>
              <p className="text-sm text-gray-600">System Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/help')}
              variant="outline"
              className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="admin-help-button"
            >
              <HelpCircle className="w-4 h-4" />
              Help
            </Button>
            {!isDataEntry && (
              <Button
                onClick={() => navigate('/admincrud')}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="admin-crud-button"
              >
                Manage All Data (CRUD)
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="families" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 bg-white border border-gray-200 p-1 rounded-lg">
            <TabsTrigger value="families" className="flex items-center gap-2" data-testid="admin-tab-families">
              <Users className="w-4 h-4" />
              Add Family (Bulk)
            </TabsTrigger>
            <TabsTrigger value="pricelists" className="flex items-center gap-2" data-testid="admin-tab-pricelists">
              <FileText className="w-4 h-4" />
              Price Lists (Bulk)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="families">
            <BulkFamilyUpload axiosConfig={axiosConfig} onUploaded={loadData} />
          </TabsContent>

          <TabsContent value="pricelists" className="space-y-6">
            <BulkPricelistUpload hospitals={hospitals} currencies={currencies} axiosConfig={axiosConfig} onUploaded={loadData} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
